import { useEffect } from 'react';
import { toast } from '@/hooks/use-toast';

interface ErrorInfo {
  message: string;
  source?: string;
  lineno?: number;
  colno?: number;
  error?: Error;
  type: 'javascript' | 'promise' | 'network' | 'chunk';
}

export const useGlobalErrorHandler = () => {
  

  const getErrorType = (error: any, message: string): ErrorInfo['type'] => {
    if (message.includes('Loading chunk') || message.includes('ChunkLoadError') || message.includes('Failed to fetch dynamically imported module')) {
      return 'chunk';
    }
    if (message.includes('fetch') || message.includes('NetworkError')) {
      return 'network';
    }
    if (error && error.name === 'TypeError' && message.includes('Cannot read properties')) {
      return 'javascript';
    }
    return 'javascript';
  };

  const getErrorSolution = (type: ErrorInfo['type']): string => {
    const solutions = {
      chunk: 'Hay una nueva versión disponible. Recarga la página (F5) para continuar.',
      network: 'Verifica tu conexión a internet e intenta nuevamente.',
      javascript: 'Error inesperado. Si persiste, recarga la página.',
      promise: 'Error en operación asíncrona. Intenta la acción nuevamente.',
    };
    return solutions[type];
  };

  const showErrorToast = (errorInfo: ErrorInfo) => {
    // Throttle más conservador para evitar spam
    if (errorInfo.type === 'chunk') {
      const now = Date.now();
      const lastChunkError = sessionStorage.getItem('lastChunkError');
      if (lastChunkError && (now - parseInt(lastChunkError)) < 30000) {
        return; // 30 segundos entre chunk errors
      }
      sessionStorage.setItem('lastChunkError', now.toString());
    }
    
    // Solo mostrar toast para errores importantes, sin forzar recargas
    if (errorInfo.type === 'chunk') {
      toast({
        title: "Error de carga",
        description: "Hubo un problema cargando la página. Intenta refrescar si persiste.",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const logError = (errorInfo: ErrorInfo) => {
    const message = errorInfo.message || '';

    // Ignore benign browser warnings (e.g., ResizeObserver)
    const benignPatterns = [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'ResizeObserver loop completed with undelivered not'
    ];
    if (benignPatterns.some((p) => message.includes(p))) {
      return;
    }

    // Skip React dev warnings and reconciliation errors
    if (message.includes("Cannot read properties of undefined (reading 'call')") ||
        message.includes('printWarning') ||
        message.includes('warnOnInvalidKey') ||
        message.includes('reconcileChildrenArray')) {
      return; // Don't log React dev warnings
    }
    
    // Solo log en desarrollo, silencioso en producción
    if (import.meta.env.DEV) {
      console.warn('🔸 Error Global:', errorInfo.type, '-', message.substring(0, 200));
    }
  };

  useEffect(() => {
    // Capturar errores JavaScript globales
    const handleError = (event: ErrorEvent) => {
      const errorInfo: ErrorInfo = {
        message: event.message,
        source: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error,
        type: getErrorType(event.error, event.message),
      };

      logError(errorInfo);

      // Solo mostrar toasts para errores importantes, sin logs adicionales
      if (errorInfo.type === 'chunk') {
        showErrorToast(errorInfo);
      }

      // Prevenir propagación
      return true;
    };

    // Caprurar promesas rechazadas no manejadas
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const errorInfo: ErrorInfo = {
        message: event.reason?.message || 'Promise rejection no manejada',
        error: event.reason,
        type: 'promise',
      };

      // Solo log mínimo, no toasts para promesas rechazadas
      if (import.meta.env.DEV) {
        console.warn('🔸 Promise rechazada:', errorInfo.message.substring(0, 50));
      }
      
      // Prevenir console spam
      event.preventDefault();
    };

    // Solo log en desarrollo para errores de recursos
    const handleResourceError = (event: Event) => {
      const target = event?.target as HTMLElement | null;
      if (!target) return;

      const src = (target as any)?.src;
      const tag = (target as any)?.tagName as string | undefined;
      const className = (target as any)?.className as string | undefined;
      
      // Only handle if src is actually undefined/null/invalid
      if (src === undefined || src === null || src === 'undefined' || src === '') {
        // Prevent this error from bubbling up and crashing React
        event.preventDefault();
        event.stopPropagation();
        
        // Set a default placeholder to prevent re-triggering
        if (tag === 'IMG') {
          (target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"%3E%3Crect width="24" height="24" fill="%23f1f5f9"/%3E%3C/svg%3E';
        }
        
        // Only log in development when we have useful info
        if (import.meta.env.DEV && (tag || className)) {
          console.warn('🔸 Recurso no cargado: undefined sin src', {
            tagName: tag,
            className
          });
        }
      }
    };

    // Registrar los event listeners
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleResourceError, true); // Capture phase para recursos

    // Cleanup
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleResourceError, true);
    };
  }, []);

  return {
    // Función para reportar errores manualmente desde componentes
    reportError: (error: Error, context?: string) => {
      const errorInfo: ErrorInfo = {
        message: error.message,
        error,
        type: 'javascript',
      };

      logError({ ...errorInfo, source: context });
      showErrorToast(errorInfo);
    },
  };
};